"use client";

import type React from "react";
import Link from "next/link";
import { ArrowRight } from "@/components/icons";
import { useCases } from "./data";
import UseCasesGrid, { type UseCaseGridItem } from "@/components/sections/shared/use-cases-grid";
import {
  ScrapingVisual,
  PriceVisual,
  SeoVisual,
  AiVisual,
  MarketVisual,
  BrandVisual,
} from "@/components/sections/shared/use-cases-grid/card-visuals";
import SectionHeader from "@/components/ui/section-header";
import { ROUTES } from "@/lib/constants";

/* Bento layout — the scraping card anchors the grid (2×2), the rest fill
   around it, each with its own self-running mini-visual (see the shared
   use-cases-grid card-visuals). */
const CARD_LAYOUT: Record<string, { visual: React.ComponentType; span?: "anchor" }> = {
  scraping: { visual: ScrapingVisual, span: "anchor" },
  price: { visual: PriceVisual },
  seo: { visual: SeoVisual },
  ai: { visual: AiVisual },
  market: { visual: MarketVisual },
  brand: { visual: BrandVisual },
};

const items: UseCaseGridItem[] = useCases
  .filter((uc) => CARD_LAYOUT[uc.id])
  .map((uc) => ({
    id: uc.id,
    label: uc.label,
    headline: uc.headline,
    description: CARD_LAYOUT[uc.id].span ? uc.description : undefined,
    href: uc.href,
    Visual: CARD_LAYOUT[uc.id].visual,
    span: CARD_LAYOUT[uc.id].span,
  }));

export default function UseCasesSection() {
  return (
    <section
      id="use-cases"
      /* No band tint: --surface-1 is pure white on the light theme and --card
         resolves through it, so a tinted band would render white cards on a
         white section. The full-bleed border-y still marks the section. */
      className="full-bleed border-border py-section border-y"
      aria-labelledby="use-cases-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <SectionHeader
              eyebrow="Use Cases"
              id="use-cases-heading"
              heading="Proxies for every use case."
              body="Power web scraping, price monitoring, brand protection, AI data collection, SEO monitoring, and automation with reliable proxy infrastructure built to keep your workflows running."
            />
          </div>

          <Link
            href={ROUTES.useCases}
            className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1.5 text-sm font-medium transition-colors"
          >
            See all use cases
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>

        <UseCasesGrid items={items} />
      </div>
    </section>
  );
}
